/**
 * Featured Events Carousel
 * ------------------------
 * Home-page snippet that surfaces upcoming featured/sponsored events with a
 * graceful fall-through to general upcoming events when curated picks are
 * thin. Mirrors the structure of FeaturedPropertiesCarousel so the home
 * page's discovery trio reads as a consistent visual band.
 */
import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faChevronLeft,
  faChevronRight,
  faMapMarkerAlt,
  faTicketAlt,
  faCoins,
} from "@fortawesome/free-solid-svg-icons";
import { useFeaturedEvents } from "../../hooks/useFeaturedEvents";
import { getEventTypeLabel } from "../../constants/events";
import { SmartImage } from "../OptimizedImage";
import { safeText } from "../../utils/formatAddress";
import "../../styles/FeaturedEvents.css";

function toJsDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value._seconds) return new Date(value._seconds * 1000);
  if (typeof value === "string" || typeof value === "number") return new Date(value);
  if (value instanceof Date) return value;
  return null;
}

function formatEventDateRange(startAt, endAt) {
  const start = toJsDate(startAt);
  if (!start || Number.isNaN(start.getTime())) return "Date TBA";
  const end = toJsDate(endAt);
  const dateOpts = { weekday: "short", month: "short", day: "numeric" };
  const timeOpts = { hour: "numeric", minute: "2-digit" };
  const startStr = `${start.toLocaleDateString(undefined, dateOpts)} · ${start.toLocaleTimeString(undefined, timeOpts)}`;
  if (end && !Number.isNaN(end.getTime())) {
    const sameDay =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate();
    if (sameDay) {
      return `${startStr} – ${end.toLocaleTimeString(undefined, timeOpts)}`;
    }
    return `${startStr} – ${end.toLocaleDateString(undefined, dateOpts)}`;
  }
  return startStr;
}

function formatPrice(cents) {
  if (!cents || cents <= 0) return "Free";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function FeaturedEventsCarousel({ limit = 6 }) {
  const { events, loading } = useFeaturedEvents(limit);
  const scrollerRef = useRef(null);

  const nudge = (dx) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, behavior: "smooth" });
  };

  if (loading || events.length === 0) return null;

  return (
    <section className="featured-events" aria-label="Featured events">
      <div className="featured-events__head">
        <div>
          <span className="featured-events__eyebrow">· What's happening on the islands ·</span>
          <h2><FontAwesomeIcon icon={faCalendarAlt} /> Featured events</h2>
          <p>Pirate-themed gatherings, treasure hunts, ticketed nights, and seasonal experiences across the USVI.</p>
        </div>
        <div className="featured-events__controls">
          <button onClick={() => nudge(-360)} aria-label="Scroll left" title="Previous events"><FontAwesomeIcon icon={faChevronLeft} /></button>
          <button onClick={() => nudge(360)}  aria-label="Scroll right" title="Next events"><FontAwesomeIcon icon={faChevronRight} /></button>
        </div>
      </div>
      <div className="featured-events__scroller" ref={scrollerRef}>
        {events.map((ev) => {
          const isTicketed = (ev.ticketPriceCents || 0) > 0;
          const cover = ev.coverImage || ev.heroImage || ev.imageUrl || null;
          return (
            <Link key={ev.id} to={`/events/${ev.id}`} className="featured-event-card">
              {cover ? (
                <div className="featured-event-card__image">
                  <SmartImage
                    src={cover}
                    alt={ev.title || "Event photo"}
                    className="featured-event-card__image-inner"
                    objectFit="cover"
                  />
                </div>
              ) : (
                <div className="featured-event-card__image featured-event-card__image--placeholder">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </div>
              )}
              <div className="featured-event-card__badges">
                <span className={`featured-event-card__type featured-event-card__type--${ev.type || "public"}`}>
                  {getEventTypeLabel(ev.type || "public")}
                </span>
                {ev.doubloonReward > 0 && (
                  <span className="featured-event-card__doubloons">
                    <FontAwesomeIcon icon={faCoins} /> +{ev.doubloonReward}
                  </span>
                )}
              </div>
              <div className="featured-event-card__body">
                <h3>{ev.title}</h3>
                <p className="featured-event-card__when">
                  <FontAwesomeIcon icon={faCalendarAlt} /> {formatEventDateRange(ev.startAt, ev.endAt)}
                </p>
                {(() => {
                  const loc = safeText(ev.location);
                  return loc && (
                  <p className="featured-event-card__where">
                    <FontAwesomeIcon icon={faMapMarkerAlt} /> {loc}
                  </p>
                ); })()}
                {ev.description && (
                  <p className="featured-event-card__excerpt">
                    {ev.description.slice(0, 140)}
                    {ev.description.length > 140 ? "…" : ""}
                  </p>
                )}
                <div className="featured-event-card__footer">
                  <span className={`featured-event-card__price ${isTicketed ? "is-paid" : "is-free"}`}>
                    <FontAwesomeIcon icon={faTicketAlt} /> {formatPrice(ev.ticketPriceCents)}
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      <div className="featured-events__footer">
        <Link to="/events" className="featured-events__cta">See all events →</Link>
      </div>
    </section>
  );
}
